import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { state } from '../../constants/hc_plandata';
import { useToast } from '../../context/ToastContext';
import { useLoading } from '../../context/LoadingContext';
import './AddEditCourtDetailsForm.scss';
import { getCompanyDataByID } from '../../services/api/CompanyApi';
import { companyaddeditcourtdetails } from '../../services/api/SuperAdminApi';

// Import your district court mapping JSON data
import { districtCourtMapping } from '../../constants/districtCourtMap';

const AddEditCourtDetailsForm = () => {
    const { id } = useParams(); // company id
    const navigate = useNavigate();
    const { showLoading, hideLoading } = useLoading();

    const [uniqueNames, setUniqueNames] = useState(['']);
    const [highCourtDetails, setHighCourtDetails] = useState([{ highCourt: '', bench: '' }]);
    const { showToast } = useToast();
    const [districtCourtDetails, setDistrictCourtDetails] = useState([{ state: '', stateCourtComplex: '', stateDistrict: '' }]);

    // Fetch existing data when component mounts
    useEffect(() => {
        if (id) {
            showLoading('Loading company data...');
        }

        const fetchCompanyData = async () => {
            try {
                const response = await getCompanyDataByID(id);
                const companyData = response.data;

                // Set unique names if they exist
                if (companyData.company.uniqueNames && companyData.company.uniqueNames.length > 0) {
                    setUniqueNames(companyData.company.uniqueNames);
                } else {
                    setUniqueNames(['']); // Default empty field
                }

                // Set high court details if they exist
                if (companyData.company.highCourt && companyData.company.highCourt.length > 0) {
                    const formattedHighCourtDetails = companyData.company.highCourt.map(hc => ({
                        highCourt: hc.highCourt.toString(),
                        bench: hc.bench.toString()
                    }));
                    setHighCourtDetails(formattedHighCourtDetails);
                } else {
                    setHighCourtDetails([{ highCourt: '', bench: '' }]); // Default empty fields
                }
                if (companyData.company.districtCourt && companyData.company.districtCourt.length > 0) {
                    const formattedDistrictCourtDetails = companyData.company.districtCourt.map(dc => ({
                        state: dc.state.toString(),
                        stateCourtComplex: dc.stateCourtComplex.toString(),
                        stateDistrict: dc.stateDistrict.toString()
                    }));
                    setDistrictCourtDetails(formattedDistrictCourtDetails);
                } else {
                    setDistrictCourtDetails([{ state: '', stateCourtComplex: '', stateDistrict: '' }]); // Default empty fields
                }
                const hasExistingData = (companyData.company.uniqueNames && companyData.company.uniqueNames.length > 0) ||
                    (companyData.company.highCourt && companyData.company.highCourt.length > 0) ||
                    (companyData.company.districtCourt && companyData.company.districtCourt.length > 0);

                if (hasExistingData) {
                    showToast('Data loaded successfully!', 'success');
                }

            } catch (error) {
                console.error('Error fetching company data:', error);
                showToast('Error loading existing data. Please try again.', 'error');
            } finally {
                hideLoading();
            }
        };

        if (id) {
            fetchCompanyData();
        } else {
            hideLoading();
        }
    }, [id]);

    const addUniqueName = () => {
        setUniqueNames([...uniqueNames, '']);
    };

    const removeUniqueName = (index) => {
        if (uniqueNames.length > 1) {
            const updated = uniqueNames.filter((_, i) => i !== index);
            setUniqueNames(updated);
        }
    };

    const updateUniqueName = (index, value) => {
        const updated = [...uniqueNames];
        updated[index] = value;
        setUniqueNames(updated);
    };

    const addHighCourtDetail = () => {
        setHighCourtDetails([...highCourtDetails, { highCourt: '', bench: '' }]);
    };

    const removeHighCourtDetail = (index) => {
        if (highCourtDetails.length > 1) {
            const updated = highCourtDetails.filter((_, i) => i !== index);
            setHighCourtDetails(updated);
        }
    };

    const updateHighCourtDetail = (index, field, value) => {
        const updated = [...highCourtDetails];
        updated[index][field] = value;

        // Reset bench when high court changes
        if (field === 'highCourt') {
            updated[index].bench = '';
        }

        setHighCourtDetails(updated);
    };

    const addDistrictCourtDetail = () => {
        setDistrictCourtDetails([...districtCourtDetails, { state: '', stateCourtComplex: '', stateDistrict: '' }]);
    };

    const removeDistrictCourtDetail = (index) => {
        if (districtCourtDetails.length > 1) {
            const updated = districtCourtDetails.filter((_, i) => i !== index);
            setDistrictCourtDetails(updated);
        }
    };

    const updateDistrictCourtDetail = (index, field, value) => {
        const updated = [...districtCourtDetails];
        updated[index][field] = value;

        // Reset dependent fields when parent changes - THIS IS THE KEY ADDITION
        if (field === 'state') {
            updated[index].stateDistrict = '';
            updated[index].stateCourtComplex = '';
        } else if (field === 'stateDistrict') {
            updated[index].stateCourtComplex = '';
        }

        setDistrictCourtDetails(updated);
    };

    const getAvailableBenches = (highCourtId) => {
        if (!highCourtId || !state[highCourtId]) return {};
        return state[highCourtId].benches;
    };

    // NEW HELPER FUNCTIONS FOR DISTRICT COURT CASCADING
    const getAvailableDistricts = (stateId) => {
        if (!stateId || !districtCourtMapping.states[stateId]) {
            return {};
        }
        return districtCourtMapping.states[stateId].districts;
    };

    const getAvailableCourtComplexes = (stateId, districtId) => {
        if (!stateId || !districtId ||
            !districtCourtMapping.states[stateId] ||
            !districtCourtMapping.states[stateId].districts[districtId]) {
            return {};
        }
        return districtCourtMapping.states[stateId].districts[districtId].courtComplexes;
    };

    const validateForm = () => {
        // Check for empty unique names
        const filteredUniqueNames = uniqueNames.filter(name => name.trim());
        if (filteredUniqueNames.length === 0) {
            showToast('Please add at least one unique name', 'error');
            return false;
        }

        // Check for duplicate unique names
        const duplicateNames = filteredUniqueNames.filter((name, index) =>
            filteredUniqueNames.indexOf(name) !== index
        );
        if (duplicateNames.length > 0) {
            showToast('Duplicate unique names found. Please remove duplicates.', 'error');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        // Filter out empty values
        const filteredUniqueNames = uniqueNames.filter(name => name.trim());
        const filteredHighCourtDetails = highCourtDetails.filter(detail =>
            detail.highCourt && detail.bench
        ).map(detail => ({
            highCourt: parseInt(detail.highCourt),
            bench: parseInt(detail.bench)
        }));

        const filteredDistrictCourtDetails = districtCourtDetails.filter(detail =>
            detail.state && detail.stateCourtComplex && detail.stateDistrict
        ).map(detail => ({
            state: detail.state,
            stateCourtComplex: detail.stateCourtComplex,
            stateDistrict: detail.stateDistrict
        }));

        const payload = {
            uniqueNames: filteredUniqueNames,
            highCourt: filteredHighCourtDetails,
            districtCourt: filteredDistrictCourtDetails
        };


        try {
            showLoading('Saving court details...');
            await companyaddeditcourtdetails(id, payload);
            hideLoading();
            showToast('Court details saved successfully!', 'success');
            setTimeout(() => {
                navigate('/home/superadmin');
            }, 2000);
        } catch (error) {
            hideLoading();
            console.error('Error saving court details:', error);
            showToast('Error saving court details. Please try again.', 'error');
        }
    };

    return (
        <div className="add-edit-court-details-container">
            <div className="add-edit-court-details-wrapper">
                <div className="add-edit-court-details-header">
                    <h2 className="add-edit-court-details-title">Add/Edit Court Details</h2>
                    <p className="add-edit-court-details-subtitle">Configure the court details for this company</p>
                </div>

                <hr className="add-edit-court-details-divider" />

                <form onSubmit={handleSubmit} className="form-content">
                    {/* Unique Names Section */}
                    <div className="form-section">
                        <h3>Unique Names</h3>
                        <div className="form-grid">
                            {uniqueNames.map((name, index) => (
                                <div key={index} className="court-det-form-group">
                                    <label htmlFor={`uniqueName-${index}`}>
                                        Unique Name {index + 1} <span className="required">*</span>
                                    </label>
                                    <div className="input-with-button">
                                        <input
                                            type="text"
                                            id={`uniqueName-${index}`}
                                            value={name}
                                            onChange={(e) => updateUniqueName(index, e.target.value)}
                                            placeholder="Enter unique name"
                                            className="text-input"
                                        />
                                        {uniqueNames.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeUniqueName(index)}
                                                className="remove-btn"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addUniqueName}
                            className="add-btn"
                        >
                            + Add Unique Name
                        </button>
                    </div>

                    {/* High Court Details Section */}
                    <div className="form-section">
                        <h3>High Court Details</h3>
                        <div className="form-grid">
                            {highCourtDetails.map((detail, index) => (
                                <div key={index} className="court-det-form-group full-width">
                                    <label>High Court Detail {index + 1}</label>
                                    <div className="high-court-inputs">
                                        <select
                                            value={detail.highCourt}
                                            onChange={(e) => updateHighCourtDetail(index, 'highCourt', e.target.value)}
                                            className="dropdown"
                                        >
                                            <option value="">Select High Court</option>
                                            {Object.entries(state).map(([key, value]) => (
                                                <option key={key} value={key}>
                                                    {value.highCourt}
                                                </option>
                                            ))}
                                        </select>

                                        <select
                                            value={detail.bench}
                                            onChange={(e) => updateHighCourtDetail(index, 'bench', e.target.value)}
                                            className="dropdown"
                                            disabled={!detail.highCourt}
                                        >
                                            <option value="">Select Bench</option>
                                            {detail.highCourt && Object.entries(getAvailableBenches(detail.highCourt)).map(([key, value]) => (
                                                <option key={key} value={key}>
                                                    {value}
                                                </option>
                                            ))}
                                        </select>

                                        {highCourtDetails.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeHighCourtDetail(index)}
                                                className="remove-btn"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addHighCourtDetail}
                            className="add-btn"
                        >
                            + Add High Court Detail
                        </button>
                    </div>

                    {/* UPDATED District Court Details Section with Cascading Dropdowns */}
                    <div className="form-section">
                        <h3>District Court Details</h3>
                        <div className="form-grid">
                            {districtCourtDetails.map((detail, index) => (
                                <div key={index} className="court-det-form-group full-width">
                                    <label>District Court Detail {index + 1}</label>
                                    <div className="district-court-inputs">
                                        {/* State Dropdown */}
                                        <select
                                            value={detail.state}
                                            onChange={(e) => updateDistrictCourtDetail(index, 'state', e.target.value)}
                                            className="dropdown"
                                        >
                                            <option value="">Select State</option>
                                            {Object.entries(districtCourtMapping.states).map(([stateId, stateData]) => (
                                                <option key={stateId} value={stateId}>
                                                    {stateData.name}
                                                </option>
                                            ))}
                                        </select>

                                        {/* District Dropdown */}
                                        <select
                                            value={detail.stateDistrict}
                                            onChange={(e) => updateDistrictCourtDetail(index, 'stateDistrict', e.target.value)}
                                            className="dropdown"
                                            disabled={!detail.state}
                                        >
                                            <option value="">Select District</option>
                                            {detail.state && Object.entries(getAvailableDistricts(detail.state)).map(([districtId, districtData]) => (
                                                <option key={districtId} value={districtId}>
                                                    {districtData.name}
                                                </option>
                                            ))}
                                        </select>

                                        {/* Court Complex Dropdown */}
                                        <select
                                            value={detail.stateCourtComplex}
                                            onChange={(e) => updateDistrictCourtDetail(index, 'stateCourtComplex', e.target.value)}
                                            className="dropdown"
                                            disabled={!detail.state || !detail.stateDistrict}
                                        >
                                            <option value="">Select Court Complex</option>
                                            {detail.state && detail.stateDistrict &&
                                                Object.entries(getAvailableCourtComplexes(detail.state, detail.stateDistrict)).map(([complexId, complexData]) => (
                                                    <option key={complexId} value={complexId}>
                                                        {complexData.name}
                                                    </option>
                                                ))}
                                        </select>

                                        {districtCourtDetails.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeDistrictCourtDetail(index)}
                                                className="remove-btn"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addDistrictCourtDetail}
                            className="add-btn"
                        >
                            + Add District Court Detail
                        </button>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate('/home/superadmin')}
                            className="btn-cancel"
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn-submit">
                            Save Court Details
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddEditCourtDetailsForm;